import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { useAuth } from "@/contexts/AuthContext";
import { MessageCircle, Pin, PinOff, Trash2, X, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";

// Long-press threshold — matches iOS/Android native long-press feel.
const LONG_PRESS_MS = 500;

// Style label mapping. Legacy values ("roleplay","adult") from pre-v36 chats
// silently fall back to "Story" — chats keep working; users can re-pick from
// inside the chat's Style tab anytime.
const STYLE_LABEL_MAP = {
  fluff: "Fluff",
  slowburn: "Slow-burn Romance",
  drama: "Angst / Drama",
  comedy: "Comedy",
  adventure: "Adventure",
  dark: "Dark / Thriller",
  grounded: "Grounded",
};
function styleLabel(id) {
  return STYLE_LABEL_MAP[id] || "Story";
}


export default function Chats() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isPlus = !!(user?.is_plus || user?.is_admin);
  const [chats, setChats] = useState([]);
  const [pinnedId, setPinnedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null); // chat awaiting delete confirmation
  const [actionSheetFor, setActionSheetFor] = useState(null); // chat whose long-press sheet is open

  // ------------ Search (Immersive+ perk) ------------
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching, [] = no hits
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!isPlus) { setSearchResults(null); return; }
    const query = q.trim();
    if (query.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/chats/search", { params: { q: query } })
        .then(({ data }) => setSearchResults(data.results || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);  // debounce
    return () => clearTimeout(t);
  }, [q, isPlus]);

  const load = () => {
    api
      .get("/chats")
      .then(({ data }) => {
        setChats(data.chats || []);
        setPinnedId(data.pinned_chat_id || null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  async function togglePin(chat) {
    try {
      const isPinned = pinnedId === chat.id;
      if (isPinned) {
        await api.delete(`/chats/${chat.id}/pin`);
        setPinnedId(null);
        toast.success("Unpinned.");
      } else {
        await api.post(`/chats/${chat.id}/pin`);
        setPinnedId(chat.id);
        toast.success("Pinned to top.");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Pin failed.");
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    try {
      await api.delete(`/chats/${target.id}`);
      setChats((prev) => prev.filter((c) => c.id !== target.id));
      if (pinnedId === target.id) setPinnedId(null);
      toast.success("Chat deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed.");
    } finally {
      setPendingDelete(null);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-4">
        <div className="font-display text-xs uppercase tracking-[0.35em] text-violet-400/80">Recent</div>
        <h1 className="font-display text-3xl leading-tight text-white">Your Chats</h1>
        <p className="mt-1 text-xs text-neutral-500">Showing your 15 most recent · long-press a chat for pin & delete.</p>
      </header>

      {/* -------- Search bar (Immersive+) -------- */}
      <div className="relative mb-5">
        <Search size={14} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          data-testid="chats-search-input"
          value={q}
          onChange={(e) => {
            if (!isPlus && e.target.value.length > 0) {
              toast.error("Search is an Immersive+ perk.");
              nav("/upgrade");
              return;
            }
            setQ(e.target.value);
          }}
          placeholder={isPlus ? "Search across all your chats…" : "Search — Immersive+ only"}
          className={`w-full rounded-2xl border bg-white/[0.03] py-2.5 pl-9 pr-8 text-sm text-white placeholder:text-neutral-500 focus:outline-none ${
            isPlus ? "border-white/10 focus:border-violet-500/50" : "border-white/5 opacity-60"
          }`}
        />
        {q && (
          <button
            data-testid="chats-search-clear"
            onClick={() => { setQ(""); setSearchResults(null); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full bg-white/5 p-1 text-neutral-400 hover:bg-white/10"
            aria-label="Clear"
          >
            <X size={12} />
          </button>
        )}
        {!isPlus && (
          <div className="mt-1 text-[10px] text-neutral-500">
            <Sparkles size={9} className="inline -mt-px" /> Upgrade to Immersive+ to search every message.
          </div>
        )}
      </div>

      {/* -------- Search results (when searching) -------- */}
      {searchResults !== null ? (
        <SearchResults results={searchResults} loading={searching} q={q} />
      ) : loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl bg-white/[0.03]" />
          ))}
        </div>
      ) : chats.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center" data-testid="chats-empty">
          <MessageCircle size={40} className="text-neutral-700" />
          <div className="mt-4 font-display text-xl text-white">No chats yet.</div>
          <p className="mt-2 max-w-xs text-sm text-neutral-500">
            Start a story by tapping a character on the Discover feed.
          </p>
          <Link
            to="/discover"
            data-testid="chats-empty-discover-btn"
            className="btn-pill btn-primary mt-6"
          >
            Explore characters
          </Link>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="chats-list">
          {chats.map((c) => (
            <ChatRow
              key={c.id}
              chat={c}
              isPinned={pinnedId === c.id}
              onLongPress={() => setActionSheetFor(c)}
            />
          ))}
        </ul>
      )}

      <BottomNav />

      {/* Long-press action sheet */}
      <Sheet open={!!actionSheetFor} onOpenChange={(v) => !v && setActionSheetFor(null)}>
        <SheetContent
          side="bottom"
          className="glass-strong border-white/10 text-white pb-8"
          data-testid="chat-action-sheet"
        >
          {actionSheetFor && (
            <>
              <div className="flex items-center gap-3 border-b border-white/5 pb-3 pt-1">
                {actionSheetFor.character?.avatar_url ? (
                  <img
                    src={actionSheetFor.character.avatar_url}
                    alt={actionSheetFor.character.name}
                    className="h-11 w-11 rounded-xl object-cover"
                  />
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/20 font-display text-lg text-violet-200">
                    {actionSheetFor.character?.name?.[0]}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg text-white truncate">
                    {actionSheetFor.character?.name || "Unknown"}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {actionSheetFor.message_count} messages
                  </div>
                </div>
              </div>

              <div className="mt-3 space-y-1.5">
                <button
                  data-testid="action-sheet-pin"
                  onClick={() => {
                    togglePin(actionSheetFor);
                    setActionSheetFor(null);
                  }}
                  className="flex w-full items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-left hover:bg-white/[0.05]"
                >
                  {pinnedId === actionSheetFor.id ? (
                    <>
                      <PinOff size={16} className="text-amber-300" />
                      <div>
                        <div className="text-sm text-white">Unpin from top</div>
                        <div className="text-[11px] text-neutral-500">Return to normal sort order.</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <Pin size={16} className="text-amber-300" />
                      <div>
                        <div className="text-sm text-white">Pin to top</div>
                        <div className="text-[11px] text-neutral-500">Keep this chat above the rest.</div>
                      </div>
                    </>
                  )}
                </button>
                <button
                  data-testid="action-sheet-delete"
                  onClick={() => {
                    setPendingDelete(actionSheetFor);
                    setActionSheetFor(null);
                  }}
                  className="flex w-full items-center gap-3 rounded-xl border border-rose-500/20 bg-rose-500/[0.06] px-4 py-3 text-left hover:bg-rose-500/[0.12]"
                >
                  <Trash2 size={16} className="text-rose-300" />
                  <div>
                    <div className="text-sm text-rose-100">Delete chat</div>
                    <div className="text-[11px] text-neutral-500">Permanently remove this conversation.</div>
                  </div>
                </button>
                <button
                  data-testid="action-sheet-cancel"
                  onClick={() => setActionSheetFor(null)}
                  className="flex w-full items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-left hover:bg-white/[0.05]"
                >
                  <X size={16} className="text-neutral-400" />
                  <div className="text-sm text-neutral-300">Cancel</div>
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <DialogContent className="glass-strong border-white/10 w-[calc(100vw-1rem)] sm:w-auto sm:max-w-sm text-white p-4 sm:p-6" data-testid="chat-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Delete this chat?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-300">
            {pendingDelete?.character?.name ? `Your conversation with ${pendingDelete.character.name} ` : "This conversation "}
            and all its messages will be permanently deleted. This can't be undone.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              data-testid="chat-delete-cancel"
              onClick={() => setPendingDelete(null)}
              className="btn-pill btn-ghost flex-1"
            >Cancel</button>
            <button
              data-testid="chat-delete-confirm"
              onClick={confirmDelete}
              className="btn-pill flex-1 bg-rose-500/20 border border-rose-500/40 text-rose-100 hover:bg-rose-500/30"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SearchResults({ results, loading, q }) {
  const nav = useNavigate();
  if (loading && !results) {
    return <div className="mt-8 text-center text-sm text-neutral-500" data-testid="chats-search-loading">Searching…</div>;
  }
  if (results.length === 0) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed border-white/10 p-8 text-center" data-testid="chats-search-empty">
        <Search size={22} className="mx-auto mb-2 text-neutral-600" />
        <div className="text-sm text-neutral-400">No messages match "{q}".</div>
      </div>
    );
  }
  const highlight = (text) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text;
    return (<>
      {text.slice(0, idx)}
      <mark className="rounded bg-violet-500/40 text-white px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>);
  };
  return (
    <ul className="space-y-2" data-testid="chats-search-results">
      {results.map((r) => (
        <li key={r.message_id}>
          <button
            data-testid={`chats-search-hit-${r.message_id}`}
            onClick={() => nav(`/chat/${r.chat_id}`)}
            className="flex w-full items-start gap-3 rounded-2xl border border-white/5 bg-white/[0.02] p-3 text-left hover:bg-white/[0.05]"
          >
            {r.character_avatar_url ? (
              <img src={r.character_avatar_url} alt="" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
            ) : (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-500/20 font-display text-lg text-violet-200">
                {r.character_name?.[0] || "?"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-display text-sm text-white truncate">{r.character_name || "Chat"}</span>
                <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-neutral-400">
                  {r.role === "user" ? "You" : "Them"}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-neutral-300 line-clamp-2 leading-snug">
                {highlight(r.snippet)}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

// A single chat row that supports long-press (touch or mouse) to open the
// action sheet. A short tap still navigates to the chat.
function ChatRow({ chat: c, isPinned, onLongPress }) {
  const nav = useNavigate();
  const timerRef = useRef(null);
  const firedRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  function begin(x, y) {
    firedRef.current = false;
    startPosRef.current = { x, y };
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      // Small haptic tick on supported devices.
      try { navigator.vibrate?.(15); } catch {}
      onLongPress();
    }, LONG_PRESS_MS);
  }
  function cancel() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }
  function move(x, y) {
    // Cancel long-press if the pointer wanders (user is scrolling / dragging).
    const dx = Math.abs(x - startPosRef.current.x);
    const dy = Math.abs(y - startPosRef.current.y);
    if (dx > 12 || dy > 12) cancel();
  }

  return (
    <li>
      <a
        href={`/chat/${c.id}`}
        role="button"
        data-testid={`chat-list-item-${c.id}`}
        onClick={(e) => {
          e.preventDefault();
          // If long-press fired, DON'T also navigate.
          if (firedRef.current) { firedRef.current = false; return; }
          nav(`/chat/${c.id}`);
        }}
        onContextMenu={(e) => { e.preventDefault(); onLongPress(); }}
        onTouchStart={(e) => {
          const t = e.touches[0];
          begin(t.clientX, t.clientY);
        }}
        onTouchMove={(e) => {
          const t = e.touches[0];
          move(t.clientX, t.clientY);
        }}
        onTouchEnd={cancel}
        onTouchCancel={cancel}
        onMouseDown={(e) => begin(e.clientX, e.clientY)}
        onMouseMove={(e) => move(e.clientX, e.clientY)}
        onMouseUp={cancel}
        onMouseLeave={cancel}
        className={`flex items-center gap-3 rounded-2xl border p-3 transition select-none active:scale-[0.99] ${
          isPinned
            ? "border-amber-400/40 bg-amber-500/[0.06] hover:bg-amber-500/[0.09]"
            : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04]"
        }`}
      >
        {c.character?.avatar_url ? (
          <img
            src={c.character.avatar_url}
            alt={c.character.name}
            className="h-14 w-14 rounded-xl object-cover pointer-events-none"
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-violet-500/20 font-display text-xl text-violet-200 pointer-events-none">
            {c.character?.name?.[0]}
          </div>
        )}
        <div className="min-w-0 flex-1 pointer-events-none">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg text-white truncate">
              {c.character?.name || "Unknown"}
            </span>
            {isPinned && (
              <span
                data-testid={`chat-pinned-badge-${c.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-400/40 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-amber-200"
              >
                <Pin size={9} /> Pinned
              </span>
            )}
            {c.character?.is_nsfw && <span className="nsfw-badge">18+</span>}
          </div>
          <div className="text-xs text-neutral-500">
            {styleLabel(c.chat_style)} · {c.message_count} messages ·{" "}
            {c.updated_at && formatDistanceToNow(new Date(c.updated_at), { addSuffix: true })}
          </div>
        </div>
      </a>
    </li>
  );
}
